import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Layers, BarChart3, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const quickStartOptions = [
  {
    title: 'Load a template into the Canvas',
    description: 'See the live risk × impact × cost × cyber matrix for a real IDP (basic-aws-idp)',
    icon: Layers,
    route: '/canvas?template=basic-aws-idp',
    primary: true,
  },
  {
    title: 'IDP Tools Catalog (live risk)',
    description:
      'Browse with real-time cyber + OSINT enrichment. Load anything straight to the matrix.',
    icon: BarChart3,
    route: '/idp-tools-catalog',
  },
  {
    title: 'Browse IDP Templates',
    description: 'Recovery slice of Score-compliant templates. One click to visualize + export.',
    icon: FileText,
    route: '/idp-templates',
  },
];

export const QuickStartSection: React.FC = () => {
  const navigate = useNavigate();

  return (
    <section className="py-16 bg-[var(--background)] border-t border-[var(--border)]">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-semibold text-[var(--foreground)] tracking-tight mb-2">
              Start with the functional core
            </h2>
            <p className="text-[var(--muted-foreground)] prose-measure mx-auto">
              The three experiences that deliver the matrix, the catalog insight, and the ready
              artifacts.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {quickStartOptions.map((option, index) => {
              const Icon = option.icon;
              return (
                <Card
                  key={index}
                  className="group border-[var(--border)] bg-[var(--card)] hover:border-[var(--accent)]/60 transition-colors cursor-pointer"
                  onClick={() => navigate(option.route)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(option.route);
                    }
                  }}
                  aria-label={option.title}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-[var(--primary)]/10 text-[var(--primary)]">
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="font-medium text-[var(--foreground)] group-hover:text-[var(--primary)] transition-colors">
                        {option.title}
                      </h3>
                    </div>
                    <p className="text-sm text-[var(--muted-foreground)] mb-4 leading-relaxed">
                      {option.description}
                    </p>
                    <Button
                      variant={option.primary ? 'primary' : 'outline'}
                      size="sm"
                      className="w-full gap-2"
                    >
                      Open
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
